import { useEffect, useState } from "react";

import { addDays, isSameDay, startOfDay } from "date-fns";

// Waits a moment past midnight so a timer that fires a few milliseconds early still lands on the
// new day.
const PAST_MIDNIGHT_MS = 1000;

/**
 * Today's date, refreshed at the next local midnight and whenever the tab becomes visible again.
 *
 * A screen that derives a rolling window from "today" re-renders on the new day without waiting for
 * the user to interact, so a page left open overnight does not keep yesterday's window.
 *
 * @returns A Date on the current local calendar day. Its identity only changes when the day does.
 */
export default function useCurrentDay(): Date {
  const [today, setToday] = useState(() => new Date());

  useEffect(() => {
    const refreshIfNewDay = () => {
      const now = new Date();
      setToday((current) => (isSameDay(current, now) ? current : now));
    };
    let timeout: ReturnType<typeof setTimeout> | undefined;
    // The timer runs on a monotonic clock while the day comes from the wall clock, so a wall clock
    // that stepped back can fire the timer on the old day. Arming again from the current time keeps
    // the hook waiting for the real midnight.
    const scheduleMidnightCheck = () => {
      const nextMidnight = addDays(startOfDay(new Date()), 1);
      timeout = setTimeout(
        () => {
          refreshIfNewDay();
          scheduleMidnightCheck();
        },
        Math.max(nextMidnight.getTime() - Date.now(), 0) + PAST_MIDNIGHT_MS,
      );
    };
    scheduleMidnightCheck();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshIfNewDay();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [today]);

  return today;
}
